/**
 * The shape of the help. Platform-free blocks the web renders as HTML and the
 * app as native views, from one source (topics.ts), so the two cannot drift.
 * Every string is a template (facts.ts): `{{key}}` quotes a label or a limit.
 */

/** The roles a topic is for. The platform manager is not part of user help. */
export type HelpRole = 'member' | 'editor' | 'admin';

/** Device sections. `web` covers every browser, installed to the home screen or not. */
export type HelpDevice = 'iphone' | 'ipad' | 'android' | 'web';

export type HelpGroup = 'start' | 'tasks' | 'roles' | 'devices' | 'faq';

/**
 * Features that exist in some builds only. A topic that `requires` one is
 * hidden until the flag is on — the help states only what the system does.
 */
export const HELP_FLAGS = {
  /** Push notifications to the native app (PR 48, merged October 2026). */
  push: true,
} as const;
export type HelpFlag = keyof typeof HELP_FLAGS;

export type HelpBlock =
  /** A paragraph. */
  | { readonly p: string }
  /** A sub-heading inside a topic. */
  | { readonly h: string }
  /** Numbered steps. */
  | { readonly steps: readonly string[] }
  /** Bullets. */
  | { readonly list: readonly string[] }
  /** A highlighted hint. */
  | { readonly note: string }
  /** Applies only on these devices; rendered as its own labelled section. */
  | { readonly on: readonly HelpDevice[]; readonly blocks: readonly HelpBlock[] };

export interface HelpTopic {
  /** URL slug (`/hilfe/<id>`), German, stable: links and context buttons point at it. */
  readonly id: string;
  readonly title: string;
  /** One sentence, for the index and the search results. */
  readonly summary: string;
  readonly group: HelpGroup;
  /** Shown first for these roles; absent = for everybody. */
  readonly roles?: readonly HelpRole[];
  /** A device section. */
  readonly device?: HelpDevice;
  /** Extra words people search with that the text does not contain. */
  readonly keywords?: readonly string[];
  readonly requires?: HelpFlag;
  readonly blocks: readonly HelpBlock[];
  /** Related topics (ids). */
  readonly see?: readonly string[];
}
